// @ts-nocheck
import { getApiClient, browserCacheBuster } from '@/lib/api/api-client';
import { Collaboration } from '@/types/collaboration';
import { CatalogItem, PaginatedResponse, BaseQueryParams, WhereFilter, getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { collaborationTransformer } from '@/lib/transformers/collaborations.transformer';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';
import { logger } from '@/lib/utils/logger';
import { getCacheDuration } from '@/lib/config/cache.config';
export interface FindCollaborationsParams extends BaseQueryParams {
  years?: string[];
  type?: string[];
}

/**
 * Transform Collaboration to CatalogItem
 */
export function collaborationToCatalogItem(collaboration: Collaboration): CatalogItem {
  return collaborationTransformer.toCatalogItem(collaboration);
}

/**
 * API service for Collaborations
 */
export class CollaborationsAPI {
  static apiClient = getApiClient();
  private static CACHE_DURATION = getCacheDuration('collaborations');

  /**
   * Get collaboration list
   */
  static async findCollaborations(
    params: FindCollaborationsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    const { page = 1, limit = 10, query, sort, years, type, ...rest } = params;

    const sortValue = getSortValue(sort, { dateField: 'createdAt' });

    // Build where query
    const where: WhereFilter = {};

    // Search query
    if (query) {
      where.title = {
        contains: query,
      };
    }

    // Year filter
    if (years && years.length > 0) {
      try {
        // Special query may be required for year filtering from date field
        // Using a simple approach here
        if (years.length === 1) {
          where.date = {
            contains: years[0],
          };
        } else {
          where.or = years.map(year => ({
            date: {
              contains: year,
            },
          }));
        }
      } catch (error) {
        logger.error('Error processing years:', error);
      }
    }

    // Type filter
    if (type && type.length > 0) {
      if (type.length === 1) {
        where.type = type[0];
      } else {
        where.type = {
          in: type,
        };
      }
    }

    // Additional where parameters
    if (rest.where) {
      Object.assign(where, rest.where);
    }

    try {

      const response = await CollaborationsAPI.apiClient.collections.collaborations.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        // TODO: type properly - getSortValue returns dynamic strings not in payload-rest-client's keyof DOC union
        sort: sortValue as never,
        ...browserCacheBuster(),
      });

      // Transform API response
      const result = response as PaginatedResponse<Collaboration>;

      // Transform to CatalogItem
      const items = result.docs.map(collaborationToCatalogItem);

      return success({
        docs: items,
        totalDocs: result.totalDocs,
        limit: result.limit,
        totalPages: result.totalPages,
        page: result.page,
        pagingCounter: result.pagingCounter,
        hasPrevPage: result.hasPrevPage,
        hasNextPage: result.hasNextPage,
        prevPage: result.prevPage,
        nextPage: result.nextPage,
      });
    } catch (error) {
      return handleApiError('findCollaborations', error);
    }
  }

  /**
   * Get collaboration detail by slug
   */
  static async findCollaborationBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Collaboration>> {
    const cacheKey = await CacheService.scopedKey(`collaboration.service_${slug}`);
    // Check cache (if skipCache is false)
    if (!skipCache) {
      const cachedData = CacheService.getFromCache(cacheKey);
      if (cachedData) {
        return success(cachedData as Collaboration);
      }
    }

    try {
      const response = await CollaborationsAPI.apiClient.collections.collaborations.find({
        where: {
          slug: {
            equals: slug,
          },
        },
        depth: 2,
        limit: 1,
      });

      const collaboration = response.docs.length > 0 ? response.docs[0] : null;

      if (collaboration) {
        CacheService.addToCache(cacheKey, collaboration, this.CACHE_DURATION);
      }

      return success(collaboration);
    } catch (error) {
      return handleApiError('findCollaborationBySlug', error);
    }
  }
}
